import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { PricingCard } from '@/components/ui/PricingCard';
import { EnterprisePricingCard } from '@/components/ui/EnterprisePricingCard';
import { CloudServiceAd } from '@/components/ui/CloudServiceAd';
import { ComparisonTable } from './ComparisonTable';
import { PRICING_PLANS, ENTERPRISE_PLANS, PACKAGE_PLANS } from '@/utils/constants';

type PlanType = 'individual' | 'enterprise' | 'partner' | 'package';
type ProductSeries = 'claude' | 'glm' | 'gpt5';

export const PricingPage: FC = () => {
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>('individual');
  const [selectedSeries, setSelectedSeries] = useState<ProductSeries>('glm');

  // 根据选择的产品系列过滤计划
  const filteredPlans = PRICING_PLANS.filter(plan => plan.category === selectedSeries);

  return (
    <>
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-blue-50">
        <Container>
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              选择适合您的订阅方案
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              从个人开发者到企业团队，我们为不同规模的用户提供专业的AI编程解决方案
            </p>
            <div className="inline-flex items-center bg-warning-100 text-warning-800 px-4 py-2 rounded-full text-sm font-medium">
              🎉 新用户专享优惠，限时抢购！优惠活动截止：2025年9月30日
            </div>
          </div>

          {/* Plan Type Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-full p-1 shadow-lg border border-gray-200 mx-4">
              <div className="flex flex-col sm:flex-row">
                <button
                  onClick={() => setSelectedPlanType('individual')}
                  className={`
                    flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap
                    ${selectedPlanType === 'individual'
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-lg">👤</span>
                  <span>个人/团队方案</span>
                </button>
                <button
                  onClick={() => setSelectedPlanType('enterprise')}
                  className={`
                    flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap
                    ${selectedPlanType === 'enterprise'
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-lg">🏢</span>
                  <span>企业定制方案</span>
                </button>
                <button
                  onClick={() => setSelectedPlanType('partner')}
                  className={`
                    flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap
                    ${selectedPlanType === 'partner'
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-lg">🤝</span>
                  <span>诚招代理合作伙伴</span>
                </button>
                <button
                  onClick={() => setSelectedPlanType('package')}
                  className={`
                    flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap
                    ${selectedPlanType === 'package'
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-lg">💰</span>
                  <span>流量包方案</span>
                </button>
              </div>
            </div>
          </div>

          {/* Individual/Team Plans */}
          {selectedPlanType === 'individual' && (
            <div className="mb-16 opacity-100">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  个人/团队方案
                </h2>
                <p className="text-gray-600 mb-8">
                  适合个人开发者和小型团队的标准化解决方案
                </p>

                {/* Product Series Toggle */}
                <div className="flex justify-center mb-8">
                  <div className="bg-white rounded-full p-1 shadow-lg border border-gray-200 inline-flex">
                    <button
                      onClick={() => setSelectedSeries('glm')}
                      className={`
                        flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 relative
                        ${selectedSeries === 'glm'
                          ? 'bg-red-500 text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }
                      `}
                    >
                      <span className="text-lg">🇨🇳</span>
                      <span>国产GLM系列</span>
                      <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">NEW</span>
                    </button>
                    <button
                      onClick={() => setSelectedSeries('gpt5')}
                      className={`
                        flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 relative
                        ${selectedSeries === 'gpt5'
                          ? 'bg-emerald-500 text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }
                      `}
                    >
                      <span className="text-lg">🚀</span>
                      <span>Codex GPT-5系列</span>
                      <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">HOT</span>
                    </button>
                    <button
                      onClick={() => setSelectedSeries('claude')}
                      className={`
                        flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-300
                        ${selectedSeries === 'claude'
                          ? 'bg-blue-500 text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }
                      `}
                    >
                      <span className="text-lg">🤖</span>
                      <span>Claude系列</span>
                    </button>
                  </div>
                </div>

                {/* Series Description */}
                <div className="max-w-2xl mx-auto mb-6">
                  {selectedSeries === 'glm' ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                        国产GLM-5 系列
                        <span className="ml-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full">超值价格</span>
                      </h3>
                      <p className="text-red-700 text-sm">
                        基于国产GLM-5技术，专注中文编程场景，轻量级快速响应，超高性价比！适合国内开发者使用，支持中文编程优化
                      </p>
                    </div>
                  ) : selectedSeries === 'gpt5' ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <h3 className="font-semibold text-emerald-800 mb-2 flex items-center">
                        Codex GPT-5 系列
                        <span className="ml-2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-full">官方代充</span>
                      </h3>
                      <p className="text-emerald-700 text-sm">
                        OpenAI官方GPT-5账号代充服务，直接充值20/200美刀到官方账号，享受GPT-5、o1最新模型和DALL-E图像生成等全部功能
                      </p>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-800 mb-2">Claude AI 系列</h3>
                      <p className="text-blue-700 text-sm">
                        基于Anthropic Claude技术，提供强大的代码生成、分析和优化能力，特别擅长复杂逻辑推理和代码重构
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className={`grid gap-8 ${
                selectedSeries === 'gpt5'
                  ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'
                  : selectedSeries === 'glm'
                  ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'
                  : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto'
              }`}>
                {filteredPlans.map((plan) => (
                  <PricingCard key={plan.id} plan={plan} />
                ))}
              </div>
            </div>
          )}

          {/* Enterprise Plans */}
          {selectedPlanType === 'enterprise' && (
            <div className="mb-16 opacity-100">
              <div className="text-center mb-12">
                <div className="inline-flex items-center bg-gradient-to-r from-blue-100 to-purple-100 px-6 py-3 rounded-full mb-4">
                  <span className="text-2xl mr-3">🏢</span>
                  <span className="text-blue-800 font-bold text-lg">企业定制方案</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  面向企业的AI研发转型解决方案
                </h2>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                  针对不同规模的研发团队，提供从技术咨询到完整落地的一站式AI转型服务，
                  助力企业实现智能化研发升级
                </p>
              </div>

              <div className="flex justify-center">
                <div className="max-w-md">
                  {ENTERPRISE_PLANS.map((plan) => (
                    <EnterprisePricingCard 
                      key={plan.id} 
                      plan={plan} 
                      featured={plan.recommended}
                    />
                  ))}
                </div>
              </div>

              {/* Enterprise Benefits */}
              <div className="mt-12 bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">企业专属服务保障</h3>
                  <p className="text-gray-600">为企业客户提供全方位的专业服务和技术支持</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-4xl mb-3">🎯</div>
                    <h4 className="font-semibold text-gray-900 mb-2">专属客户经理</h4>
                    <p className="text-sm text-gray-600">一对一服务，深度了解企业需求</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">⚡</div>
                    <h4 className="font-semibold text-gray-900 mb-2">7×24小时支持</h4>
                    <p className="text-sm text-gray-600">全天候技术支持，确保业务连续性</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">🛡️</div>
                    <h4 className="font-semibold text-gray-900 mb-2">企业级SLA</h4>
                    <p className="text-sm text-gray-600">99.9%以上可用性保障</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">🔧</div>
                    <h4 className="font-semibold text-gray-900 mb-2">定制化开发</h4>
                    <p className="text-sm text-gray-600">根据企业需求定制专属功能</p>
                  </div>
                </div>

                <div className="text-center mt-8">
                  <Button
                    as={Link}
                    to="/contact"
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
                  >
                    预约企业方案咨询
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Package Plans */}
          {selectedPlanType === 'package' && (
            <div className="mb-16 opacity-100">
              <div className="text-center mb-12">
                <div className="inline-flex items-center bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-3 rounded-full mb-4">
                  <span className="text-2xl mr-3">💰</span>
                  <span className="text-green-800 font-bold text-lg">灵活流量包方案</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  按需购买，灵活使用
                </h2>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                  不想订阅月付套餐？流量包方案让您按需购买，用多少算多少，
                  适合临时项目、学习测试或非连续性使用场景
                </p>
              </div>

              <div className="flex justify-center">
                <div className="max-w-md">
                  {PACKAGE_PLANS.map((plan) => (
                    <div key={plan.id} className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-green-400 shadow-2xl">
                      {/* Badge */}
                      <div className="text-center mb-6">
                        <div className="inline-flex items-center bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-full mb-4">
                          <span className="font-bold">{plan.badge}</span>
                        </div>
                        <h3 className="text-3xl font-bold text-green-800 mb-2">{plan.name}</h3>
                        <p className="text-gray-600">{plan.description}</p>
                      </div>

                      {/* Pricing */}
                      <div className="bg-white/70 backdrop-blur rounded-xl p-6 mb-6">
                        <div className="text-center mb-4">
                          <div className="text-5xl font-bold text-green-700 mb-2">
                            ¥{plan.price}
                          </div>
                          <div className="text-gray-600">一次性支付</div>
                        </div>

                        <div className="space-y-3 border-t border-gray-200 pt-4">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">💎 总流量额度</span>
                            <span className="font-bold text-green-700 text-xl">${plan.totalQuota}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">⏰ 有效期</span>
                            <span className="font-bold text-green-700">{plan.validDays}天 (3个月)</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">💻 并发终端</span>
                            <span className="font-bold text-green-700">5</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">🤖 支持模型</span>
                            <span className="font-bold text-green-700 text-sm">Claude Sonnet、Oups</span>
                          </div>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <span className="text-green-500 mr-2">✨</span>
                          包含功能
                        </h4>
                        <ul className="space-y-2">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                              <span className="text-sm text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Limitations */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <span className="text-orange-500 mr-2">⚠️</span>
                          注意事项
                        </h4>
                        <ul className="space-y-2">
                          {plan.limitations.map((limitation, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <span className="text-orange-500 mt-0.5 flex-shrink-0">•</span>
                              <span className="text-sm text-gray-600">{limitation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA Button */}
                      <Button
                        as={Link}
                        to="/contact"
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-4 text-lg shadow-lg"
                      >
                        立即购买流量包
                      </Button>

                      {/* Value Proposition */}
                      <div className="mt-6 bg-green-100 border border-green-300 rounded-lg p-4">
                        <div className="text-center">
                          <div className="text-sm font-semibold text-green-800 mb-2">💡 性价比计算</div>
                          <p className="text-xs text-green-700">
                            ¥480 = $500流量额度，超值兑换
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            90天（3个月）内灵活使用，用完即止
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Use Cases */}
              <div className="mt-12 bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">适用场景</h3>
                  <p className="text-gray-600">流量包最适合以下使用场景</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">🎓</div>
                    <h4 className="font-semibold text-gray-900 mb-2">学习测试</h4>
                    <p className="text-sm text-gray-600">尝试AI编程工具，学习新技术</p>
                  </div>
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">⏱️</div>
                    <h4 className="font-semibold text-gray-900 mb-2">临时项目</h4>
                    <p className="text-sm text-gray-600">短期项目开发，完成即止</p>
                  </div>
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">🔄</div>
                    <h4 className="font-semibold text-gray-900 mb-2">非连续使用</h4>
                    <p className="text-sm text-gray-600">偶尔需要AI辅助编程</p>
                  </div>
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">💰</div>
                    <h4 className="font-semibold text-gray-900 mb-2">预算有限</h4>
                    <p className="text-sm text-gray-600">不想长期订阅，按需付费</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Partner Program */}
          {selectedPlanType === 'partner' && (
            <div className="mb-16 opacity-100">
              <div className="text-center mb-12">
                <div className="inline-flex items-center bg-gradient-to-r from-orange-100 to-red-100 px-6 py-3 rounded-full mb-4">
                  <span className="text-2xl mr-3">🤝</span>
                  <span className="text-orange-800 font-bold text-lg">诚招代理合作伙伴</span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  与白茆AI携手共创AI编程新时代
                </h2>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                  寻找志同道合的合作伙伴，共同推广AI编程技术，分享丰厚收益，
                  助力更多开发者和企业实现AI转型升级
                </p>
              </div>

              {/* Partner Benefits */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-200 mb-12">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">代理合作优势</h3>
                  <p className="text-gray-600">成为白茆AI代理伙伴，享受专属权益和丰厚回报</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">💰</div>
                    <h4 className="font-semibold text-gray-900 mb-2">丰厚佣金回报</h4>
                    <p className="text-sm text-gray-600">推广成功即可获得高额佣金，持续收益保障</p>
                  </div>
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">🎯</div>
                    <h4 className="font-semibold text-gray-900 mb-2">专属营销支持</h4>
                    <p className="text-sm text-gray-600">提供营销物料、技术培训和销售指导</p>
                  </div>
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">🚀</div>
                    <h4 className="font-semibold text-gray-900 mb-2">技术产品优势</h4>
                    <p className="text-sm text-gray-600">基于Claude最新技术，产品竞争力强</p>
                  </div>
                  <div className="text-center bg-white rounded-xl p-6 shadow-md">
                    <div className="text-4xl mb-3">🤝</div>
                    <h4 className="font-semibold text-gray-900 mb-2">长期合作关系</h4>
                    <p className="text-sm text-gray-600">建立稳定的战略合作伙伴关系</p>
                  </div>
                </div>

                {/* Partner Type - Full Width */}
                <div className="w-full">
                  <div className="bg-white rounded-xl p-8 shadow-xl border-3 border-orange-400">
                      <div className="text-center mb-6">
                        <div className="text-6xl mb-4">👥</div>
                        <h4 className="text-3xl font-bold text-gray-900 mb-2">渠道代理商</h4>
                        <p className="text-gray-600 text-lg">面向个人和小团队用户推广</p>
                      </div>
                      <ul className="space-y-3 text-gray-700 mb-8 text-lg">
                        <li>• 推广个人/团队套餐</li>
                        <li>• <span className="font-bold text-orange-600">佣金比例：50%</span></li>
                        <li>• 提供营销培训支持</li>
                        <li>• 客户关系长期维护</li>
                        <li>• 月度销售激励奖励</li>
                      </ul>
                      <div className="text-center">
                        <Button
                          as={Link}
                          to="/contact"
                          size="lg"
                          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-lg py-4"
                        >
                          申请成为渠道代理
                        </Button>
                      </div>
                    </div>
                </div>
              </div>

              {/* Partner Requirements */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">合作伙伴要求</h3>
                  <p className="text-gray-600">我们期望与具备以下条件的伙伴合作</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-4xl mb-3">🎓</div>
                    <h4 className="font-semibold text-gray-900 mb-2">技术背景</h4>
                    <p className="text-sm text-gray-600">具备软件开发或IT行业经验，了解AI技术发展趋势</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">🌐</div>
                    <h4 className="font-semibold text-gray-900 mb-2">客户资源</h4>
                    <p className="text-sm text-gray-600">拥有开发者社群、企业客户资源或相关渠道</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">💪</div>
                    <h4 className="font-semibold text-gray-900 mb-2">推广能力</h4>
                    <p className="text-sm text-gray-600">具备产品推广、销售经验或强烈的合作意愿</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">📈</div>
                    <h4 className="font-semibold text-gray-900 mb-2">业务规模</h4>
                    <p className="text-sm text-gray-600">有一定的业务基础，能够承担推广目标和责任</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">🤝</div>
                    <h4 className="font-semibold text-gray-900 mb-2">合作诚信</h4>
                    <p className="text-sm text-gray-600">诚信经营，愿意建立长期稳定的合作关系</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-3">⚡</div>
                    <h4 className="font-semibold text-gray-900 mb-2">执行力强</h4>
                    <p className="text-sm text-gray-600">具备良好的执行力和学习能力，快速适应市场</p>
                  </div>
                </div>

                <div className="text-center mt-8">
                  <p className="text-gray-700 mb-4">
                    <strong>特别欢迎：</strong>系统集成商、软件服务商、IT咨询公司、开发者社区领袖、技术培训机构等加入我们的合作伙伴计划
                  </p>
                  <Button
                    as={Link}
                    to="/contact"
                    size="lg"
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white shadow-lg"
                  >
                    立即申请合作
                  </Button>
                </div>
              </div>
            </div>
          )}

            {/* Model Performance Info */}
          <div className="mb-12">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="text-3xl">📊</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">模型性能对比</h3>
                  <p className="text-gray-700">
                    <span className="font-semibold text-primary-600">Opus 4.1</span> 在 SWE-bench Verified（软件工程基准）上得分为 
                    <span className="font-bold text-xl text-primary-600 mx-1">74.5%</span>， 比 
                    <span className="font-semibold">Sonnet、Oups</span>（62.3%）高出 
                    <span className="font-bold text-green-600 mx-1">~12%</span> 
                     
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    💡  注意：Opus使用的Tokens消耗是Sonnet得5倍, <span className="font-semibold">Max</span> 和 <span className="font-semibold">Max Plus</span> 套餐用户可畅享 Opus 4.0/4.1 模型的强大性能
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Cloud Service Ad */}
      <section className="py-16 bg-white">
        <Container>
          <CloudServiceAd />
        </Container>
      </section>

      {/* Detailed Comparison */}
      <section className="py-16 bg-gray-50">
        <Container>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              功能详细对比
            </h2>
            <p className="text-gray-600">
              深入了解各套餐的功能差异，选择最适合您的方案
            </p>
          </div>
          
          <ComparisonTable plans={filteredPlans} />
        </Container>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-white">
        <Container>
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            使用场景推荐
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            从个人开发者到大型企业团队，为不同规模和需求提供最适合的AI编程解决方案
          </p>
          
          {/* Individual Plans */}
          {/* Individual Plans */}
          {selectedPlanType === 'individual' && (
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">个人/团队方案</h3>

              {/* GPT-5 Series */}
              {selectedSeries === 'gpt5' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 shadow-lg border-2 border-emerald-300">
                    <div className="text-4xl mb-4 text-center">🔥</div>
                    <h4 className="text-xl font-bold text-emerald-900 mb-3 text-center">官方Plus - 轻度使用者</h4>
                    <ul className="space-y-2 text-gray-600 text-sm mb-6">
                      <li>• 个人日常使用</li>
                      <li>• 学习和体验GPT-5</li>
                      <li>• 20美刀官方额度</li>
                      <li>• DALL-E图像生成</li>
                      <li>• 高级语音模式</li>
                    </ul>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-emerald-600">¥150</span>
                      <span className="text-gray-600"> /次</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-8 shadow-lg border-2 border-violet-300">
                    <div className="text-4xl mb-4 text-center">🏆</div>
                    <h4 className="text-xl font-bold text-violet-900 mb-3 text-center">官方Pro - 深度使用者</h4>
                    <ul className="space-y-2 text-gray-600 text-sm mb-6">
                      <li>• 商业项目开发</li>
                      <li>• 大规模AI应用</li>
                      <li>• 200美刀官方额度</li>
                      <li>• 优先体验新功能</li>
                      <li>• 专属代充支持</li>
                    </ul>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-violet-600">¥1600</span>
                      <span className="text-gray-600"> /次</span>
                    </div>
                  </div>
                </div>
              )}

              {/* GLM Series */}
              {selectedSeries === 'glm' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8 shadow-lg border-2 border-red-300">
                    <div className="text-4xl mb-4 text-center">🇨🇳</div>
                    <h4 className="text-xl font-bold text-red-900 mb-3 text-center">GLM Coding Max - 高性价比</h4>
                    <ul className="space-y-2 text-gray-600 text-sm mb-6">
                      <li>• 中文编程优化</li>
                      <li>• 328官方拼车</li>
                      <li>• 每日不限量使用</li>
                      <li>• 轻量级快速响应</li>
                      <li>• 国产化技术栈</li>
                    </ul>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-red-600">¥328</span>
                      <span className="text-gray-600"> /月</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-8 shadow-lg border-2 border-orange-300">
                    <div className="text-4xl mb-4 text-center">🏅</div>
                    <h4 className="text-xl font-bold text-orange-900 mb-3 text-center">GLM 官方独享 - 官方直供</h4>
                    <ul className="space-y-2 text-gray-600 text-sm mb-6">
                      <li>• 智谱AI官方直供</li>
                      <li>• 官方专属通道</li>
                      <li>• 更稳定的服务保障</li>
                      <li>• 官方技术支持</li>
                      <li>• 企业级SLA保障</li>
                    </ul>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-orange-600">¥469</span>
                      <span className="text-gray-600"> /月</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Claude Series */}
              {selectedSeries === 'claude' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  <div className="bg-white rounded-xl p-8 shadow-lg">
                    <div className="text-4xl mb-4 text-center">🚗</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">拼车 - 高性价比</h4>
                    <ul className="space-y-2 text-gray-600 text-sm mb-6">
                      <li>• 拼车共享，超值畅享</li>
                      <li>• Claude全模型(含Opus)</li>
                      <li>• 每日约$60使用额度</li>
                      <li>• 日常编程辅助首选</li>
                    </ul>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-primary-600">¥658</span>
                      <span className="text-gray-600">/月</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-amber-500 relative">
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-amber-500 text-white px-3 py-1 text-sm font-semibold rounded-full">
                        推荐
                      </span>
                    </div>
                    <div className="text-4xl mb-4 text-center">🏅</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">独享账号 - 重度/团队</h4>
                    <ul className="space-y-2 text-gray-600 text-sm mb-6">
                      <li>• 独享整号，不与他人共享</li>
                      <li>• 每日无限制请求</li>
                      <li>• 最多5个终端并发</li>
                      <li>• 专属客服一对一</li>
                    </ul>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-amber-600">¥2380</span>
                      <span className="text-gray-600">/月</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-lg">
                    <div className="text-4xl mb-4 text-center">🎟️</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">流量包 - 按需灵活</h4>
                    <ul className="space-y-2 text-gray-600 text-sm mb-6">
                      <li>• $500总流量额度</li>
                      <li>• 90天(3个月)有效期</li>
                      <li>• 用完即止，灵活使用</li>
                      <li>• 临时/非连续场景</li>
                    </ul>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-green-700">¥480</span>
                      <span className="text-gray-600">/3个月</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Enterprise Solutions */}
          {selectedPlanType === 'enterprise' && (
            <div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">企业定制解决方案</h3>
              <div className="flex justify-center">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 shadow-xl border-2 border-blue-400 relative max-w-lg">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-3 py-1 text-sm font-bold rounded-full">
                      推荐
                    </span>
                  </div>
                  <div className="text-4xl mb-4 text-center">🏢</div>
                  <h4 className="text-2xl font-bold text-blue-800 mb-3 text-center">企业定制方案</h4>
                  <p className="text-blue-600 text-lg mb-6 text-center">5+人研发团队</p>
                  <ul className="space-y-3 text-blue-700 mb-6">
                    <li>• 包含Max Plus全部功能</li>
                    <li>• 专属客户经理服务</li>
                    <li>• 定制化API集成方案</li>
                    <li>• 团队管理和权限控制</li>
                    <li>• 企业级安全保障</li>
                    <li>• 私有化部署选项</li>
                    <li>• 7×24小时技术支持</li>
                    <li>• 企业级SLA保障</li>
                  </ul>
                  <div className="text-center">
                    <p className="text-gray-600 mb-4">价格根据团队规模和需求定制</p>
                    <Button
                      as={Link}
                      to="/contact"
                      size="lg"
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                    >
                      获取企业方案报价
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Partner Program Use Cases */}
          {selectedPlanType === 'partner' && (
            <div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">代理合作机会</h3>
              <div className="w-full">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-10 shadow-2xl border-3 border-orange-400">
                    <div className="text-center mb-8">
                      <div className="text-6xl mb-4">👥</div>
                      <h4 className="text-3xl font-bold text-orange-800 mb-2">渠道代理商</h4>
                      <p className="text-orange-600 text-xl">个人/团队市场推广</p>
                    </div>
                    <div className="space-y-6 text-gray-700 mb-8">
                      <div className="bg-white rounded-lg p-6 shadow-md">
                        <h5 className="font-semibold text-gray-900 mb-3 text-lg">🎯 目标客户</h5>
                        <p className="text-lg">个人开发者、小型团队、技术爱好者、编程学习者</p>
                      </div>
                      <div className="bg-white rounded-lg p-6 shadow-md">
                        <h5 className="font-semibold text-gray-900 mb-3 text-lg">💰 收益潜力</h5>
                        <p className="text-lg">月推广10个用户，每月可获得 <span className="font-bold text-orange-600">8,000-16,000元</span> 佣金</p>
                      </div>
                      <div className="bg-white rounded-lg p-6 shadow-md">
                        <h5 className="font-semibold text-gray-900 mb-3 text-lg">🚀 推广渠道</h5>
                        <p className="text-lg">技术社区、开发者论坛、社交媒体、线下技术分享</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <Button
                        as={Link}
                        to="/contact"
                        size="lg"
                        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-xl py-5"
                      >
                        了解渠道代理详情
                      </Button>
                    </div>
                </div>
              </div>

              {/* Success Stories */}
              <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
                <div className="text-center mb-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">合作成功案例</h4>
                  <p className="text-gray-600">真实合作伙伴的成功经验分享</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <div className="text-center mb-4">
                      <div className="text-3xl mb-2">👨‍💻</div>
                      <h5 className="font-semibold text-gray-900">张先生 - 技术博主</h5>
                      <p className="text-sm text-gray-600">3个月推广业绩</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">推广用户：</span>28人</p>
                      <p><span className="font-medium">月均收益：</span>¥13,600</p>
                      <p><span className="font-medium">推广渠道：</span>技术博客 + 微信群</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <div className="text-center mb-4">
                      <div className="text-3xl mb-2">🎯</div>
                      <h5 className="font-semibold text-gray-900">王女士 - 开发者社区</h5>
                      <p className="text-sm text-gray-600">社区推广案例</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">推广用户：</span>45人</p>
                      <p><span className="font-medium">月均收益：</span>¥21,800</p>
                      <p><span className="font-medium">推广渠道：</span>开发者社区 + 线下活动</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <div className="text-center mb-4">
                      <div className="text-3xl mb-2">👥</div>
                      <h5 className="font-semibold text-gray-900">李先生 - 培训机构</h5>
                      <p className="text-sm text-gray-600">教育机构合作</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">培训学员：</span>120人注册</p>
                      <p><span className="font-medium">转化率：</span>85%</p>
                      <p><span className="font-medium">月收益：</span>¥24,000+</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Container>
      </section>


      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
        <Container>
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">
              还有疑问？立即联系我们
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              专业客服团队为您提供一对一咨询服务
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                as={Link}
                to="/contact"
                size="lg"
                className="bg-white text-primary-600 hover:bg-gray-100"
              >
                联系客服购买
              </Button>
              <Button
                as={Link}
                to="/docs"
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary-600"
              >
                查看安装教程
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};